import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { QueryModule} from "@/data/invalidateQueries";
import { type InvalidateQueryOptions, invalidateQueries } from "@/data/invalidateQueries";
import type { AppMutationOptions, AppQueryOptions } from "@/types/react-query";

import { CryptoApi } from "./crypto.api";
import type { CryptoModels } from "./crypto.models";

export namespace CryptoQueries {
  export const moduleName = 'CryptoQueries';

  export const keys = {
    all: [moduleName] as const,
    disperseReward: () => [...keys.all, "/crypto/disperse-reward"] as const,
    getDisperseRewardStatus: () => [...keys.all, "/crypto/disperse-reward-status"] as const,
    getSymbols: () => [...keys.all, "/crypto/symbols"] as const,
    getPositions: () => [...keys.all, "/crypto/positions"] as const,
  };

  /**
   * Mutation `useDisperseReward`
   * @summary Disperse crypto reward to user
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<CryptoModels.DisperseRewardResponse> }
   * @statusCodes [200, 401]
   */
  export const useDisperseReward = (
    options?: AppMutationOptions<typeof CryptoApi.disperseReward, { data: CryptoModels.DisperseRewardRequest }> &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: ({ data }) => CryptoApi.disperseReward(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName as QueryModule, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Query `useGetDisperseRewardStatus`
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<CryptoModels.GetDisperseRewardStatusResponse> }
   * @statusCodes [200, 401]
   */
  export const useGetDisperseRewardStatus = <TData>(
    options?: AppQueryOptions<typeof CryptoApi.getDisperseRewardStatus, TData>,
  ) => {
    return useQuery({
      queryKey: keys.getDisperseRewardStatus(),
      queryFn: CryptoApi.getDisperseRewardStatus,
      ...options,
    });
  };

  /**
   * Query `useGetPositions`
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<CryptoModels.GetCryptoPositionsResponse> }
   * @statusCodes [200, 401]
   */
  export const useGetPositions = <TData>(options?: AppQueryOptions<typeof CryptoApi.getPositions, TData>) => {
    return useQuery({
      queryKey: keys.getPositions(),
      queryFn: CryptoApi.getPositions,
      ...options,
    });
  };

  /**
   * Query `useGetSymbols`
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<CryptoModels.GetCryptoSymbolsResponse> }
   * @statusCodes [200, 401]
   */
  export const useGetSymbols = <TData>(options?: AppQueryOptions<typeof CryptoApi.getSymbols, TData>) => {
    return useQuery({
      queryKey: keys.getSymbols(),
      queryFn: CryptoApi.getSymbols,
      ...options,
    });
  };
}
